import React, { FunctionComponent, useState } from 'react'
import { Avatar, Heading, Loader, Text } from '@afterpaytouch/core'
import { authLogout, setAllowedQueryParam, useAllowedQuery, useAppDispatch, useAppRouter, useConsumerName, useConsumerSelector } from '../../state'
import { Route } from '../../config/router'
import { useTranslation } from '../../utils/convergence'
import { TrackOnMount, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import { useCheckoutSizes } from '../../utils/responsive'
import { MaskedElement } from '@afterpaytouch/integrations/MaskedElement'
import styles from './style.module.css'

/**
 * CASHDAP-3223: the "Welcome back" recognition transient (Variant A) shown on the LoggedIn
 * interstitial when a returning AP-cohort consumer is silently recognized via their Auto Remember Me
 * cookie. It's a brief branded screen — avatar, name, "Recognized on this device", spinner — that
 * displays while the existing silent-login/start-checkout flow completes and auto-advances to payment.
 * Gated by useShouldShowWelcomeBackRecognition (flag + recognized + AP-eligible).
 *
 * CASHDAP-3083: includes the "Not you?" escape hatch — a mis-recognized consumer can end the
 * remember-me session (authLogout → closeSession clears the cookie server-side) and drop into the full
 * sign-in flow with an empty identity field. The risk step-up (207 → verify) is handled upstream by
 * persistentLoginOrchestrator, which routes an `active && verify` resume to Route.VERIFY before this
 * transient ever renders.
 */
interface WelcomeBackRecognitionProps {
  /**
   * CASHDAP-3083: called synchronously when "Not you?" is tapped, before the async logout, so the
   * LoggedIn page can cancel any in-flight post-login auto-advance and not race the sign-out.
   */
  onSignOut?: () => void
}

export const WelcomeBackRecognition: FunctionComponent<WelcomeBackRecognitionProps> = ({ onSignOut }) => {
  const { t } = useTranslation()
  const consumerName = useConsumerName()
  const { email, profile } = useConsumerSelector()
  const dispatch = useAppDispatch()
  const router = useAppRouter()
  const allowedQuery = useAllowedQuery()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const [isSigningOut, setIsSigningOut] = useState(false)
  // Prefer the consumer profile's dedicated givenNames field (accountLookup stores it under
  // state.profile); fall back to the first token of the full display name when it isn't populated.
  // accountLookup copies givenNames verbatim, so treat blank/whitespace-only (e.g. surname-only
  // profiles) as missing via the trimmed truthy check rather than ?? (which keeps an empty string).
  // The display name is itself getConsumerName(givenNames + surname), so trim/collapse it before
  // taking the first token — a whitespace-only givenNames would otherwise leave a leading-space name.
  const firstName = profile?.givenNames?.trim() || consumerName.trim().split(/\s+/)[0]
  const { textSize, headingSize, smallTextSize } = useCheckoutSizes()

  const onNotYou = async (): Promise<void> => {
    setIsSigningOut(true)
    // Cancel LoggedIn's post-login auto-advance synchronously (before the async logout) so a fast
    // in-flight /start can't route the mis-recognized user into the remembered account's checkout.
    onSignOut?.()
    logEvent(TrackingEvent.PRESSED_NOT_YOU_BUTTON)
    await dispatch(authLogout())
    // "Sign in as someone else": authLogout doesn't clear consumer.email/mobile, and the login form
    // seeds its identity from those, so without skipPrefill the login lands with the *same* remembered
    // account prefilled. Set skipPrefill so the identity field starts empty.
    dispatch(setAllowedQueryParam({ ...allowedQuery, skipPrefill: true }))
    router.push(Route.ENTER_EMAIL)
  }

  return (
    <div className={styles.container} data-testid='auto-remember-me-welcome-back'>
      <TrackOnMount eventName={TrackingEvent.VIEWED_AUTO_REMEMBER_ME_PAGE} />
      <MaskedElement actionName='Auto Remember Me Welcome Back'>
        <Avatar size='M' src='' alt={consumerName} name={consumerName} title={consumerName} />
        <Heading size={headingSize}>{t('common:rememberMeWelcomeBack', { name: firstName })}</Heading>
        <Text size={textSize}>{email}</Text>
      </MaskedElement>
      <div className={styles.recognized}>
        <span className={styles.check} aria-hidden='true'>
          ✓
        </span>
        <Text size={smallTextSize}>{t('common:rememberMeRecognizedDevice')}</Text>
      </div>
      <Loader />
      <Text size={textSize}>{t('common:rememberMeLoggingInSecurely')}</Text>
      <button type='button' className={styles.notYou} onClick={() => void onNotYou()} disabled={isSigningOut} data-testid='auto-remember-me-not-you'>
        {t('common:rememberMeNotYou')}
      </button>
    </div>
  )
}
